import React from 'react';
import { ChevronLeft, ChevronRight, Minus, Plus, X } from 'lucide-react';
import { isDateInRange, isDateSelectable } from './shoppingCartAssistantCalendarUtils';
import type { Group, ShoppingCartData } from './shoppingCartTypes';

export type ShoppingCartAssistantTravelSectionProps = {
  t: (key: string) => string;
  /** Kept for API compat — section is always expanded in the stepped UI. */
  isTravelDurationExpanded: boolean;
  setIsTravelDurationExpanded: (v: boolean) => void;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  setSelectedStartDate: (d: Date | null) => void;
  setSelectedEndDate: (d: Date | null) => void;
  plannedNights: number;
  onPlannedNightsChange: (nights: number) => void;
  currentMonth: Date;
  groups: Group[];
  onContinue?: (data: ShoppingCartData) => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  handleDateClick: (day: number) => void;
  monthName: string;
  days: Array<number | null>;
};

function formatShort(d: Date) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ShoppingCartAssistantTravelSection({
  t,
  selectedStartDate,
  selectedEndDate,
  setSelectedStartDate,
  setSelectedEndDate,
  plannedNights,
  onPlannedNightsChange,
  currentMonth,
  groups,
  onContinue,
  handlePrevMonth,
  handleNextMonth,
  handleDateClick,
  monthName,
  days,
}: ShoppingCartAssistantTravelSectionProps) {
  const awaitingFirstDay = !selectedStartDate;
  const awaitingLastDay = !!selectedStartDate && !selectedEndDate;

  /** Soft preview of end while waiting for second click (from duration). */
  const previewEnd =
    awaitingLastDay && selectedStartDate
      ? (() => {
          const end = new Date(
            selectedStartDate.getFullYear(),
            selectedStartDate.getMonth(),
            selectedStartDate.getDate(),
          );
          end.setDate(end.getDate() + plannedNights);
          return end;
        })()
      : null;

  const rangeEnd = selectedEndDate ?? previewEnd;

  const guideText = awaitingFirstDay
    ? t('shoppingCart.chooseFirstDay') || '1. First day'
    : awaitingLastDay
      ? t('shoppingCart.chooseLastDay') || '2. Last day'
      : null;

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-[#3d2817]">
          {t('shoppingCart.duration') || 'Duration'}
        </label>
        <div className="flex items-center overflow-hidden rounded-xl border border-[#3d2817]/15 bg-white/90">
          <button
            type="button"
            onClick={() => onPlannedNightsChange(plannedNights - 1)}
            disabled={plannedNights <= 1}
            className="flex items-center justify-center bg-[#f5f1eb] px-3 py-2.5 transition-colors hover:bg-[#e8ddd7] disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Decrease nights"
          >
            <Minus className="h-4 w-4 text-[#3d2817]" />
          </button>
          <span className="flex-1 select-none py-2.5 text-center text-sm font-semibold text-[#3d2817]">
            {plannedNights}{' '}
            {plannedNights === 1
              ? t('guestReservation.night') || 'night'
              : t('guestReservation.nights') || 'nights'}
          </span>
          <button
            type="button"
            onClick={() => onPlannedNightsChange(plannedNights + 1)}
            className="flex items-center justify-center bg-[#f5f1eb] px-3 py-2.5 transition-colors hover:bg-[#e8ddd7]"
            aria-label="Increase nights"
          >
            <Plus className="h-4 w-4 text-[#3d2817]" />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-[#5d4437]">
          {t('shoppingCart.durationHint') ||
            'Adjust nights anytime — or tap two days on the calendar to set a period.'}
        </p>
      </div>

      {(selectedStartDate || selectedEndDate) && (
        <div className="relative rounded-xl border border-[#2f9e44]/25 bg-[#2f9e44]/08 px-3 py-2.5 text-sm text-[#3d2817]">
          <button
            type="button"
            onClick={() => {
              setSelectedStartDate(null);
              setSelectedEndDate(null);
              onContinue?.({ startDate: null, endDate: null, groups });
            }}
            className="absolute top-2 right-2 rounded-full p-1 text-[#3d2817]/45 transition-colors hover:bg-white/60 hover:text-[#3d2817]"
            title={t('common.clear')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <p className="pr-6">
            <span className="font-semibold">{t('shoppingCart.startDate')}: </span>
            {selectedStartDate ? formatShort(selectedStartDate) : '—'}
            <span className="mx-2 text-[#3d2817]/35">→</span>
            <span className="font-semibold">{t('shoppingCart.endDate')}: </span>
            {selectedEndDate
              ? formatShort(selectedEndDate)
              : previewEnd
                ? `${formatShort(previewEnd)}*`
                : '—'}
          </p>
        </div>
      )}

      <div className="relative">
        {/* Guiding bubble */}
        {guideText ? (
          <div
            className="pointer-events-none absolute -top-1 left-1/2 z-20 -translate-x-1/2 -translate-y-full"
            role="status"
          >
            <div
              className={`relative flex items-center gap-2 rounded-2xl border bg-white px-3 py-2 shadow-lg ${
                awaitingFirstDay ? 'border-[#2f9e44]/40' : 'border-[#3d2817]/35'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                  awaitingFirstDay ? 'bg-[#2f9e44]' : 'bg-[#3d2817]'
                }`}
              >
                {awaitingFirstDay ? '1' : '2'}
              </span>
              <p className="whitespace-nowrap text-xs font-bold text-[#3d2817]">{guideText}</p>
              <span
                className={`absolute top-full left-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border-r border-b bg-white ${
                  awaitingFirstDay ? 'border-[#2f9e44]/40' : 'border-[#3d2817]/35'
                }`}
                aria-hidden
              />
            </div>
          </div>
        ) : null}

        <div className="overflow-hidden rounded-2xl border border-[#3d2817]/12 bg-white/90 shadow-sm">
          <div className="flex items-center justify-between border-b border-[#3d2817]/08 px-2 py-2">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#3d2817] transition-colors hover:bg-[#2f9e44]/12"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h4 className="font-felix-titling text-sm font-medium uppercase tracking-wide text-[#3d2817]">
              {monthName}
            </h4>
            <button
              type="button"
              onClick={handleNextMonth}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#3d2817] transition-colors hover:bg-[#2f9e44]/12"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="px-2 pb-3 pt-2">
            <div className="mb-1 grid grid-cols-7 gap-0.5">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div
                  key={`day-header-${index}`}
                  className="py-1 text-center text-[10px] font-semibold uppercase text-[#3d2817]/45"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-y-1">
              {days.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="flex h-9 items-center justify-center" />;
                }

                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                const selectable = isDateSelectable(date);
                const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                const startNormalized = selectedStartDate
                  ? new Date(
                      selectedStartDate.getFullYear(),
                      selectedStartDate.getMonth(),
                      selectedStartDate.getDate(),
                    )
                  : null;
                const endNormalized = rangeEnd
                  ? new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate())
                  : null;

                const isStart =
                  !!startNormalized && dateNormalized.getTime() === startNormalized.getTime();
                const isEnd =
                  !!endNormalized && dateNormalized.getTime() === endNormalized.getTime();
                const inRange = isDateInRange(date, selectedStartDate, rangeEnd);
                const isPreviewOnly = awaitingLastDay && isEnd;
                const isToday = date.toDateString() === new Date().toDateString();

                return (
                  <div key={day} className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handleDateClick(day)}
                      disabled={!selectable}
                      className={[
                        'flex h-9 w-9 items-center justify-center text-sm font-medium transition-colors',
                        !selectable
                          ? 'cursor-not-allowed text-[#3d2817]/25'
                          : isStart
                            ? 'rounded-full bg-[#2f9e44] text-white ring-2 ring-[#2f9e44]/40 ring-offset-1'
                            : isEnd && !isPreviewOnly
                              ? 'rounded-full bg-[#3d2817] text-white ring-2 ring-[#3d2817]/30 ring-offset-1'
                              : isPreviewOnly
                                ? 'rounded-full bg-[#3d2817]/20 text-[#3d2817] ring-1 ring-dashed ring-[#3d2817]/45'
                                : inRange
                                  ? 'rounded-none bg-[#2f9e44]/15 text-[#3d2817]'
                                  : isToday
                                    ? 'rounded-full font-bold text-[#2f9e44] ring-1 ring-[#2f9e44]/35'
                                    : 'rounded-full text-[#3d2817] hover:bg-[#2f9e44]/15',
                      ].join(' ')}
                    >
                      {day}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
